import { useCallback, useEffect, useMemo, useState } from 'react';

import { IconButton } from 'components/Button';
import CircularProgress from 'components/CircularProgress';
import { DialogContent, DialogRoot, DialogTitle } from 'components/Dialog';
import Grid from 'components/Grid';
import Icon from 'components/Icon';
import Typography from 'components/Typography';
import { useAppContext } from 'context/AppContext';
import { revealSEOUrl } from 'helpers/SEOHelper';
import { shouldRenderSeoFacetHref } from 'helpers/filters';

import FormControlLabel from '../FormControlLabel';

import classes from './Dialog.module.scss';

const noop = () => {};
const defaultObject = {};

/** @type {import("./types").default} */
const Dialog = ({
  dialogFacet = '',
  disabled,
  isOpen = false,
  onClose = noop,
  onItemClick = noop,
  title = '',
  id = '',
  attributes = defaultObject,
  isSeoFriendly = true,
  categoryHasSeoFacets = true,
}) => {
  const selections = useMemo(() => {
    const initialSections = {};
    Object.keys(attributes).forEach((key) =>
      attributes[key].forEach(({ isChecked, value }) => {
        initialSections[value] = isChecked;
      })
    );
    return initialSections;
  }, [attributes]);

  const [selectedItems, setSelectedItems] = useState(selections);

  useEffect(() => {
    setSelectedItems(selections);
  }, [attributes, selections]);

  const handleCheckboxClick = useCallback(
    (event, { isChecked, link, value, href }) => {
      if (link) {
        window.location.href = revealSEOUrl(link);
      } else if (href) {
        onItemClick(event, {
          facet: dialogFacet,
          href: revealSEOUrl(href),
          isChecked,
          sectionId: id,
          value,
        });
      } else {
        setSelectedItems((selected) => ({ ...selected, [value]: isChecked }));
        onItemClick(event, { isChecked, sectionId: id, value });
      }
    },
    [dialogFacet, id, onItemClick]
  );

  const { route, query } = useAppContext();

  return (
    <DialogRoot
      className="Dialog"
      onClose={onClose}
      aria-labelledby="filter-dialog"
      isOpen={isOpen}
      fullWidth={true}
      maxWidth="sm"
      data-testid="FilterDialog"
      isSeoFriendly={isSeoFriendly}
    >
      <DialogTitle
        className={classes.dialogTitleRoot}
        id="confirmation-dialog-title"
      >
        <div className={classes.titleContainer}>
          <Typography variant="heading-sm" className={classes.textVariation}>
            {title}
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeIcon}
            onClick={onClose}
          >
            <Icon icon="times" size="24" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className={classes.dialogContentRoot} dividers={true}>
        {Object.keys(attributes).map((key) => (
          <div key={`${key}-container-for-sticky`}>
            <div className={classes.typographyContainer}>
              <Typography
                key={`${key}-title`}
                variant="heading-sm"
                className={classes.textVariationLetter}
              >
                {key}
              </Typography>
            </div>
            <Grid
              key={`${key}-title-grid`}
              className={classes.gridContainerRoot}
              container={true}
            >
              {attributes[key].map(
                ({ label, value, href, selected, count, is_seo }) => (
                  <Grid
                    key={`${key}-${label}-grid-item`}
                    item={true}
                    xs={12}
                    sm={6}
                  >
                    <FormControlLabel
                      disabled={disabled}
                      key={label}
                      onClick={(event, { isChecked }) =>
                        handleCheckboxClick(event, {
                          href: revealSEOUrl(href),
                          value,
                          isChecked,
                        })
                      }
                      checkboxColor="primary"
                      type="text"
                      href={
                        shouldRenderSeoFacetHref({
                          isSeo: is_seo,
                          route,
                          query,
                          categoryHasSeoFacets,
                        })
                          ? revealSEOUrl(href)
                          : undefined
                      }
                      label={
                        <div className={classes.filterRow}>
                          <span>{label}</span>
                          {!selected && count && !disabled && (
                            <span className={classes.filterCount}>
                              ({count})
                            </span>
                          )}
                          {disabled && (
                            <CircularProgress
                              className={classes.circularProgress}
                              color="inherit"
                              size={16}
                            />
                          )}
                        </div>
                      }
                      value={value}
                      isChecked={selectedItems[value] || selected}
                    />
                  </Grid>
                )
              )}
            </Grid>
          </div>
        ))}
      </DialogContent>
    </DialogRoot>
  );
};

export default Dialog;
